import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { COUNTDOWN_PLACEHOLDER, useCountdown } from '@renderer/utils/countdownUtils'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import Spinner from '@renderer/components/Spinner'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { FaUndo, FaCreditCard } from 'react-icons/fa'
import { Package } from '@renderer/types/configState'
import { useTranslation } from 'react-i18next'
import { TransactionStatus } from '@renderer/types/sessionState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import Button from '@renderer/components/Button'
import { formatAmount, normalizeCurrency, roundMoney } from '@renderer/utils/formatCurrency'
import {
  closeToyyibpayBill,
  hasToyyibpayBillApi,
  openToyyibpayBill
} from '@renderer/lib/runtime/bridge'

// Prices are stored natively (RM for MY projects). The ringgit figure is shown
// as-is; for the Midtrans cross-border QRIS rail (which settles in IDR) the
// actual rupiah charge comes back from the payment API and is shown as a hint.
// ToyyibPay bills MYR directly, so it shows ringgit only.

const PaymentPage: React.FC = () => {
  type PaymentProvider = 'midtrans' | 'toyyibpay' | 'mercadopago'
  const { t } = useTranslation()
  const { isCompact: paymentDesignCompact } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const paymentNarrowLandscape = viewportCompact && isLandscapeViewport
  const paymentTight = paymentDesignCompact || paymentNarrowLandscape
  /** Short foldables / landscape: cap QR box and scroll the glass panel so nothing clips */
  const paymentGlass = paymentNarrowLandscape
    ? 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-md min-h-0 flex-col items-center gap-2 overflow-y-auto px-4 py-3'
    : paymentTight
      ? 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-xl min-h-0 flex-col items-center gap-3 overflow-y-auto px-6 py-4'
      : 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-2xl min-h-0 flex-col items-center gap-4 overflow-y-auto p-12'
  const paymentQrBox = paymentNarrowLandscape
    ? 'h-[min(9rem,32vh)] w-[min(9rem,32vh)] min-h-0 shrink-0'
    : paymentTight
      ? 'h-[min(15rem,42vh)] w-[min(15rem,42vh)] min-h-0 shrink-0'
      : 'h-[min(25rem,48vh)] w-[min(25rem,48vh)] min-h-0 shrink-0'
  const paymentRedirectBox = paymentNarrowLandscape
    ? 'h-[min(22rem,75vh)] w-full max-w-sm min-h-0 shrink-0'
    : paymentTight
      ? 'h-[min(30rem,70vh)] w-full max-w-md min-h-0 shrink-0'
      : 'h-[min(36rem,70vh)] w-full max-w-md min-h-0 shrink-0'
  const paymentTitle = paymentNarrowLandscape ? 'text-lg' : paymentTight ? 'text-2xl' : 'text-4xl'
  const paymentHint = paymentNarrowLandscape
    ? 'text-xs text-center text-gray-600'
    : paymentTight
      ? 'text-sm text-center text-gray-600'
      : 'text-base text-center text-gray-600'
  const paymentRemainingTimeClass = paymentNarrowLandscape
    ? 'text-center font-mono tabular-nums text-base text-carbon'
    : paymentTight
      ? 'text-center font-mono tabular-nums text-xl text-carbon'
      : 'text-center font-mono tabular-nums text-3xl text-carbon'
  const paymentBtnCompact = paymentNarrowLandscape
    ? '!px-3 !py-1.5 !text-xs'
    : paymentTight
      ? '!px-4 !py-2 !text-sm'
      : '!px-6 !py-2.5 !text-base'
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const { primaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const config = globalConfig?.pages.payment
  const welcomeSettings = getPageSettings('welcome')
  const isMounted = useRef(true)

  /** Display currency (write-time stamp from the backend). */
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  /** True when the project's tier renders QRIS without a custom gateway. */
  const tier = globalConfig?.paymentSetupTier
  const hasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  const isVoucherOnlyRegion =
    tier === 'global_hidden' ||
    (tier === 'localized_in_progress' && !hasCustomGateway)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [snapToken, setSnapToken] = useState<string | null>(null)
  const [clientKey, setClientKey] = useState<string | null>(null)
  const [paymentType, setPaymentType] = useState<'core' | 'snap' | 'redirect'>('core')
  const [activeProvider, setActiveProvider] = useState<PaymentProvider>('midtrans')
  // The amount/currency the gateway actually bills, from the payment API. For a
  // ringgit project on cross-border QRIS this is the rupiah charge; for
  // ToyyibPay it's the ringgit amount.
  const [chargeInfo, setChargeInfo] = useState<{ amount: number; currency: string } | null>(null)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [billWindowOpen, setBillWindowOpen] = useState(false)
  const [billWindowError, setBillWindowError] = useState<string | null>(null)
  const billWindowOpenedForUrlRef = useRef<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  // Flips to `true` as soon as the /api/payments call resolves (success or
  // failure). Used to hide the QRIS-themed payment panel during the initial
  // request so non-QRIS rails (e.g. ToyyibPay → redirect popup) never flash
  // the QRIS logo / countdown / QR box on the way to the gateway's hosted
  // page. QRIS rails simply see the panel appear once the QR is ready,
  // which actually feels snappier than today's "empty QR slot then QR".
  const [paymentInitialized, setPaymentInitialized] = useState(false)
  const [showSkipGuide, setShowSkipGuide] = useState(false)
  const [snapPaymentOpened, setSnapPaymentOpened] = useState(false)
  const [showCodeEntry, setShowCodeEntry] = useState(false)
  // Customer voucher entry (NOT the operator PIN). Wired in-place on the
  // Payment page so the event-template flow (Welcome → Payment, with
  // `startWithPayment: true`) works without a Checkout step to fall back
  // to. Validated via `/api/promos/apply`, same as Checkout's promo input.
  const [showVoucherInput, setShowVoucherInput] = useState(false)
  const [voucherError, setVoucherError] = useState('')
  const [isVoucherLoading, setIsVoucherLoading] = useState(false)
  const [isOnline, setIsOnline] = useState(true)
  const [networkError, setNetworkError] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const countdownPausedAtRef = useRef<number | null>(null)
  const countdownPausedSecondsRef = useRef<number>(0)
  const isOnlineRef = useRef<boolean>(true)
  const { selectedSecondaryCamera, password } = useSettingsStore()
  const {
    id,
    transaction,
    setSessionState,
    userPreferences,
    mode,
    logActivity
  } = useSessionStateStore()
  const sessionReset = useSessionReset()
  const navigate = useNavigate()
  const { projectId } = useParams()

  // Initialize payment countdown
  const paymentCountdown = useCountdown(0) // We'll set the actual seconds when payment is initiated

  // Initialize auto-reset countdown (15 seconds)
  const autoResetCountdown = useCountdown(15)

  // Set up auto-reset countdown callbacks
  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })

    autoResetCountdown.setOnComplete(() => {
      sessionReset()
    })
  }, [])

  // Watch for payment expiration to start auto-reset countdown
  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    // Check if the session for which this timer was started is still the active session.
    if (id !== useSessionStateStore.getState().id) {
      logActivity('Ignoring timeout for a previous session.')
      return
    }

    // Only trigger expiration if online - if offline, pause countdown instead
    if (!isOnline) {
      safeUpdate(() => {
        paymentCountdown.stop()
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        logActivity('Payment countdown paused - network disconnected', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      })
      return
    }

    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      logActivity('Payment timed out.', {
        orderId: transaction.orderId,
        paymentType
      })
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })
    })
  }

  // Set up payment countdown callbacks
  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      // Only update countdown display if online
      if (isOnline) {
        setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
      }
    })

    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [isOnline])

  const getDefaultPrice = (): number => {
    const packages = globalConfig?.pages.checkout?.settings?.packages || []
    // `Array.prototype.filter` doesn't narrow `(Package | undefined)[]` to
    // `Package[]` on its own — use an explicit type predicate so the
    // reducer below sees a non-null `pkg` and TS stays happy.
    const isPackage = (pkg: Package | undefined): pkg is Package => pkg !== undefined
    let selectedPackages: Package[] = []

    if (mode === 'record') {
      selectedPackages = [packages.find((pkg) => pkg.type === 'video')].filter(isPackage)
    } else {
      // `mode === 'capture'` OR undefined: the event-template flow
      // (Welcome → Payment with `startWithPayment: true`) reaches this
      // page before any Capture/Record step has set `mode`, so an
      // undefined mode used to fall through both arms and return 0 —
      // which displayed as "₹ 0.00" in voucher-only regions. Treat
      // undefined as capture (the universal default) so the
      // first-impression price is meaningful.
      selectedPackages = [
        packages.find((pkg) => pkg.type === 'print'),
        packages.find((pkg) => pkg.type === 'gif'),
        packages.find((pkg) => pkg.type === 'recording')
      ].filter(isPackage)
    }

    return selectedPackages.reduce((subtotal, pkg) => subtotal + (pkg.price || 0), 0)
  }

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }, [transaction.orderId, transaction.status])

  useEffect(() => {
    // Wait for state updates to propagate (especially from Checkout page)
    const initializePayment = async (): Promise<void> => {
      // Use microtask to let state updates from Checkout propagate
      await Promise.resolve()

      // Get latest state inside async function to avoid stale closure
      const latestState = useSessionStateStore.getState()
      const latestTransaction = latestState.transaction

      if (latestTransaction.amount !== undefined && latestTransaction.amount !== null) {
        console.log(
          `[PAYMENT] Amount already set from Checkout: Rp${latestTransaction.amount} (discount: ${latestTransaction.discount || 0})`
        )
        console.log(`[PAYMENT] Transaction data from Checkout:`, {
          amount: latestTransaction.amount,
          discount: latestTransaction.discount,
          status: latestTransaction.status,
          selectedPackages: latestTransaction.selectedPackages?.length || 0
        })
        stateUploader.uploadSessionState()
        return
      }

      console.warn('[PAYMENT] Amount not set, calculating fallback amount')
      let calculatedAmount: number

      // If selectedPackages exist, calculate from them (accounts for user selections)
      if (latestTransaction.selectedPackages && latestTransaction.selectedPackages.length > 0) {
        const subtotal = latestTransaction.selectedPackages.reduce(
          (sum, pkg) => sum + roundMoney(pkg.price || 0, projectCurrency),
          0
        )
        const discount = roundMoney(latestTransaction.discount || 0, projectCurrency)
        calculatedAmount = roundMoney(Math.max(0, subtotal - discount), projectCurrency)
      } else {
        // Fallback to default price if no packages selected
        calculatedAmount = getDefaultPrice()
      }

      // Only update if amount was truly missing
      console.log(`[PAYMENT] Fallback: Setting calculated amount: Rp${calculatedAmount}`)
      setSessionState({
        transaction: {
          amount: calculatedAmount
        }
      })
      console.log(`[PAYMENT] Transaction amount set in state:`, { amount: calculatedAmount })

      // Wait another microtask for state update to propagate before uploading
      await Promise.resolve()

      // Upload session state after ensuring correct amount
      stateUploader.uploadSessionState()
    }

    initializePayment().catch((error) => {
      console.error('Error initializing payment:', error)
      // Fallback: try to upload anyway
      stateUploader.uploadSessionState()
    })

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  // Handle orderId changes
  useEffect(() => {
    // When component unmounts or orderId changes, cleanup previous payment if needed
    return (): void => {
      if (transaction.orderId && transaction.status !== 'settlement') {
        cleanupPayment()
      }
    }
  }, [transaction.orderId, cleanupPayment])

  // useEffect(() => {
  //   // You can also change below url value to any script url you wish to load,
  //   const midtransScriptUrl = 'https://app.midtrans.com/snap/snap.js'

  //   const scriptTag = document.createElement('script')
  //   scriptTag.src = midtransScriptUrl

  //   document.body.appendChild(scriptTag)

  //   return (): void => {
  //     document.body.removeChild(scriptTag)
  //   }
  // }, [])

  const handleSnapPaymentSuccess = async (result: unknown): Promise<void> => {
    console.log('Snap payment successful:', result)
    setIsCheckingPayment(false)
    cogoToast.success(t('payment.successMessage', 'Payment successful!'))
    logActivity('Payment successful via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      snapResult: result
    })
    setSessionState({
      transaction: {
        status: 'settlement'
      }
    })
    stateUploader.uploadSessionState()
    const filterSettings = globalConfig?.pages.filter?.settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    let nextPage = welcomeSettings?.startWithPayment
      ? applyAfterCapture
        ? 'frame'
        : 'filter'
      : 'capture'
    // Check for secondary camera - only go to camera if startWithPayment is true
    // (because if startWithPayment is false, camera was already selected in Welcome)
    if (
      welcomeSettings?.startWithPayment &&
      selectedSecondaryCamera &&
      (await unifiedCameraUtils.isDeviceResponding('secondary'))
    ) {
      nextPage = 'camera'
    }
    navigate(`/${projectId}/${nextPage}`)
  }

  const handleSnapPaymentError = (result: unknown): void => {
    console.error('Snap payment error:', result)
    if (isOnlineRef.current) {
      cogoToast.error(t('payment.snapError', 'Payment failed. Please try again.'))
    }
    logActivity('Payment failed via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      error: result,
      level: 'error'
    })
    // P1-3: use sessionReset so the failed transaction, gallery URLs and
    // session logs are cleaned up before returning to Welcome — otherwise
    // the next customer would inherit the previous orderId/transaction.
    sessionReset()
  }

  const handleSnapPaymentClose = (): void => {
    setSnapPaymentOpened(false)
    // Continue checking payment status in case user completed payment
    setIsCheckingPayment(true)
  }

  // Open ToyyibPay bill page in a real child BrowserWindow (popup). Webview
  // embedding is unreliable across Electron versions, and toyyibPay refuses
  // iframe embedding via X-Frame-Options. The popup uses real Chromium, so
  // it renders identically to opening the URL in a regular browser. Polling
  // we already do will detect payment completion automatically.
  const openBillWindow = useCallback(
    async (urlOverride?: string): Promise<void> => {
      const url = urlOverride || redirectUrl
      if (!url) return
      // Bridge helper returns status='unavailable' when running outside an
      // Electron shell with the bill-window IPC wired up (e.g. Tauri/web).
      if (!hasToyyibpayBillApi()) {
        setBillWindowError(
          t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
        )
        return
      }

      try {
        setBillWindowError(null)
        setBillWindowOpen(true)
        billWindowOpenedForUrlRef.current = url
        const result = await openToyyibpayBill(url)
        // Window has closed by the time the promise resolves (or it was a
        // duplicate request that was just refocused, or the bridge said the
        // host doesn't expose the IPC).
        if (result.status === 'reused') {
          // Already had a window open; treat as still open.
          return
        }
        setBillWindowOpen(false)
        if (result.status === 'unavailable') {
          setBillWindowError(
            t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
          )
          logActivity('ToyyibPay bill window unavailable', {
            url,
            level: 'error',
            reason: result.reason
          })
        } else if (result.status === 'failed') {
          setBillWindowError(
            t(
              'payment.popupFailed',
              'Could not open the payment window. Please try again.'
            )
          )
          logActivity('ToyyibPay bill window failed to load', {
            url,
            level: 'error',
            reason: result.reason,
            finalUrl: result.finalUrl
          })
        } else if (result.status === 'completed') {
          logActivity('ToyyibPay bill window completed', {
            url,
            finalUrl: result.finalUrl
          })
          // Polling already running will pick up the settlement; nothing to do.
        } else {
          logActivity('ToyyibPay bill window closed by user', {
            url,
            finalUrl: result.finalUrl
          })
        }
      } catch (error) {
        setBillWindowOpen(false)
        const message = error instanceof Error ? error.message : 'unknown'
        setBillWindowError(
          t('payment.popupFailed', 'Could not open the payment window. Please try again.')
        )
        console.error('[Payment] openToyyibpayBill failed', error)
        logActivity('ToyyibPay bill window error', {
          url,
          error: message,
          level: 'error'
        })
      }
    },
    [redirectUrl, t]
  )

  const openSnapPayment = async (): Promise<void> => {
    if (!snapToken || !clientKey || snapPaymentOpened) return

    try {
      window.snap.pay(snapToken, {
        onSuccess: (result: unknown) => {
          console.log('Snap payment success:', result)
          handleSnapPaymentSuccess?.(result)
        },
        onError: (result: unknown) => {
          console.error('Snap payment error:', result)
          handleSnapPaymentError?.(result)
        },
        onClose: () => {
          console.log('Snap payment popup closed')
          handleSnapPaymentClose?.()
        }
      })
    } catch (error) {
      console.error('Error opening Snap payment:', error)
      throw error
    }
  }

  const initiatePayment = async (): Promise<void> => {
    // Already settled (e.g. cash voucher): skip payment and navigate to next page
    if (transaction.status === 'settlement') {
      // No /api/payments call happens, so flip the loading gate
      // immediately to release the rendering guard below before we
      // navigate away.
      setPaymentInitialized(true)
      const filterSettings = globalConfig?.pages.filter?.settings
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      const nextPage = welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : 'capture'
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    // Voucher-only region: never call /api/payments. We render an
    // in-place "Enter Voucher Code" button on the Payment page itself
    // (see `renderPaymentInterface` below) instead of redirecting back
    // to Checkout — the event-template flow goes Welcome → Payment
    // directly (`startWithPayment: true`) and never sees a Checkout
    // step, so a redirect would bounce the customer onto an unfamiliar
    // page. The operator-PIN modal stays reserved for the offline-mode
    // bypass and must never gate the voucher-only flow.
    if (isVoucherOnlyRegion) {
      // Surface the voucher-entry UI right away (no payment call to wait for).
      setPaymentInitialized(true)
      logActivity('Payment voucher-only region — awaiting customer voucher input', {
        paymentSetupTier: tier
      })
      return
    }

    // Check network status before initiating payment
    if (!isOnline) {
      setNetworkError(true)
      // Surface the offline UI (which lives inside the panel) — no
      // network call will fire to flip the gate from the success path.
      setPaymentInitialized(true)
      logActivity('Payment initiation blocked - no internet connection', {
        isOnline: false
      })
      return
    }

    // Reset network error state when attempting to initiate
    setNetworkError(false)

    try {
      // `grossAmount` MUST be in the project's native currency — the backend
      // does the FX conversion to settlement currency (Midtrans cross-border
      // QRIS → IDR, ToyyibPay → MYR). On the first call `transaction.amount`
      // is still the native value (set from Checkout / `getDefaultPrice`).
      // On retry it has already been overwritten with the SETTLEMENT amount
      // (e.g. IDR 67014 for an MYR project) by the session writer below, so
      // reading it raw would re-feed an IDR figure as MYR and the backend
      // would multiply it by the FX rate again — a 3500× overcharge.
      // Always prefer `nativeAmount` (preserved separately) when present.
      const nativeGrossAmount =
        transaction.nativeAmount ?? transaction.amount ?? getDefaultPrice()
      const data = await MidtransUtils.initiatePayment(
        nativeGrossAmount,
        id || '',
        config?.settings.paymentTimeout || 120,
        projectId || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        transaction.discount || 0,
        false
      )

      if (data.order_id) {
        // Normalise the gateway the backend resolved this charge to. Mercado
        // Pago (Checkout Pro QR) comes back as `payment_type: 'core'` like
        // Midtrans QRIS, so it renders through the same on-screen QR path.
        const resolvedProvider: PaymentProvider =
          data.provider === 'toyyibpay'
            ? 'toyyibpay'
            : data.provider === 'mercadopago'
              ? 'mercadopago'
              : 'midtrans'
        setPaymentType(data.payment_type)
        setActiveProvider(resolvedProvider)
        setChargeInfo({
          amount: Number(data.amount) || 0,
          currency: (data.charge_currency || data.currency || projectCurrency).toUpperCase()
        })
        setRedirectUrl(data.redirect_url || null)

        // Calculate initial remaining time
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        // Session contract: `amount` + `currency` track the GATEWAY
        // SETTLEMENT (Midtrans → IDR, ToyyibPay → MYR), NOT the project's
        // display currency. `data.amount` / `data.charge_currency` come
        // back from /api/payments with the actual gateway charge. We
        // preserve the native price the customer saw on
        // `nativeAmount` / `nativeCurrency` so dashboards can surface
        // "RM 14.50 (charged as Rp 49,300)" reconciliation, and stash
        // `fxRate` for finance to recompute from. Same-currency rails
        // (IDR project on Midtrans) get matching native + settlement.
        const settlementAmount =
          typeof data.amount === 'number' ? data.amount : Number(transaction.amount) || 0
        // Settlement currency is a free-string from the payment API
        // (gateway-supplied); the kiosk's `LocalCurrency` union covers
        // every code the backend can return today, so coerce via
        // `normalizeCurrency` to keep the type narrow.
        const settlementCurrency = normalizeCurrency(
          data.charge_currency || data.currency || projectCurrency
        )
        // Use the same `nativeGrossAmount` we just sent to the backend, not
        // `transaction.amount` — the latter has already been overwritten with
        // the prior call's SETTLEMENT amount on retry, so persisting it as
        // `nativeAmount` would corrupt the cross-currency reconciliation
        // (dashboards would see e.g. "RM 67014" instead of "RM 19").
        const nativeAmount = Number(nativeGrossAmount)
        const sessionData = {
          transaction: {
            orderId: data.order_id,
            walletId: data.wallet_id || 'dreambooth',
            provider: resolvedProvider,
            qrUrl: '',
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus,
            amount: settlementAmount,
            currency: settlementCurrency,
            nativeAmount,
            nativeCurrency: projectCurrency,
            ...(typeof data.fx_rate === 'number' ? { fxRate: data.fx_rate } : {}),
            discount: transaction.discount || 0,
            paymentType: data.payment_type
          }
        }
        console.log(
          `[PAYMENT] Payment initiated - transaction amount: Rp${transaction.amount} (discount: ${transaction.discount || 0})`
        )
        logActivity('Payment initiated.', {
          amount: transaction.amount,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds,
          paymentType: data.payment_type
        })

        if (data.payment_type === 'core' && data.qr_url) {
          // Core API with QR code
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          sessionData.transaction.qrUrl = sanitizedQrUrl
        } else if (data.payment_type === 'snap' && data.snap_token) {
          // Snap payment
          setSnapToken(data.snap_token)
          setClientKey(data.client_key || '')
        }

        setSessionState(sessionData)
        setIsExpired(false)

        // Stop any existing countdown and start new one
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()

        // Reset countdown pause tracking
        countdownPausedAtRef.current = null
        countdownPausedSecondsRef.current = 0

        setIsCheckingPayment(true)
        isMounted.current = true
      }
    } catch (e: unknown) {
      // Backend declined QRIS for this region — render the in-place
      // voucher input on the Payment page (no redirect; see the
      // pre-`/api/payments` guard above for the same contract).
      if (e instanceof VoucherOnlyRegionError) {
        logActivity('Payment switched to voucher-only (backend 409)', {
          paymentSetupTier: e.paymentSetupTier
        })
        return
      }
      const error = e as Error
      const isNetworkError =
        !isOnline || error.message.includes('fetch') || error.message.includes('network')

      if (isNetworkError) {
        setNetworkError(true)
        logActivity('Payment initiation failed - network error', {
          error: error.message,
          isOnline,
          level: 'error'
        })
      } else {
        logActivity(`Payment initiation failed: ${error.message}`, {
          error: error.message,
          level: 'error'
        })
      }
      console.error('Error initiating payment:', e)
      // Only show error if online and not in offline mode
      if (isOnlineRef.current) {
        cogoToast.error(<span>{error.message}</span>)
      }
    } finally {
      // `paymentInitialized` gates the QRIS-themed panel chrome. Flip it
      // regardless of branch — success leaves the panel to render either
      // the QR (Midtrans/core) or a transparent spinner (ToyyibPay/redirect,
      // gated separately by `useStraightRedirectUI`); failures still need
      // the panel so the customer can see the error / timer / retry path.
      setPaymentInitialized(true)
    }
  }

  // Check internet connectivity for offline mode
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  useEffect(() => {
    if (isOnline) {
      initiatePayment()
    }

    return (): void => {
      // Removed logActivity to keep activity logs clean - no payment-related logs outside payment page
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
      // Best-effort close of the toyyibPay popup when leaving the page;
      // bridge helper no-ops outside Electron.
      closeToyyibpayBill()
    }
  }, [isOnline])

  // Auto-open the ToyyibPay bill popup as soon as a redirect URL is
  // available. Only opens once per URL so re-renders don't spawn duplicates.
  useEffect(() => {
    if (paymentType !== 'redirect') return
    if (!redirectUrl) return
    if (isExpired) return
    if (transaction.status === 'settlement') return
    if (billWindowOpenedForUrlRef.current === redirectUrl) return
    openBillWindow(redirectUrl)
  }, [paymentType, redirectUrl, isExpired, transaction.status, openBillWindow])

  // If the payment completes (settlement) or expires while the popup is
  // still open, close it so the user isn't left staring at a dead bill page.
  useEffect(() => {
    const shouldClose = isExpired || transaction.status === 'settlement'
    if (!shouldClose) return
    // Bridge helper is a safe no-op outside Electron.
    closeToyyibpayBill()
  }, [isExpired, transaction.status])

  // Handle network status transitions
  useEffect(() => {
    if (!isOnline) {
      // Network went offline - pause countdown and polling
      if (paymentCountdown.getRemainingSeconds() > 0) {
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        paymentCountdown.stop()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        setIsCheckingPayment(false)
        logActivity('Network disconnected during payment', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      }
    } else {
      // Network came back online - resume countdown and polling if payment was in progress
      if (countdownPausedAtRef.current !== null && countdownPausedSecondsRef.current > 0) {
        const pausedAt = countdownPausedAtRef.current
        const pausedSeconds = countdownPausedSecondsRef.current
        const elapsedSeconds = Math.floor((Date.now() - pausedAt) / 1000)
        const adjustedRemainingSeconds = Math.max(0, pausedSeconds - elapsedSeconds)

        if (adjustedRemainingSeconds > 0 && transaction.orderId) {
          paymentCountdown.setRemainingSeconds(adjustedRemainingSeconds)
          paymentCountdown.start()
          setIsCheckingPayment(true)
          setNetworkError(false)
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          logActivity('Network reconnected during payment', {
            orderId: transaction.orderId,
            adjustedRemainingSeconds,
            elapsedSeconds
          })
        } else {
          // Time expired while offline, check payment status
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          checkPaymentStatus()
        }
      } else if (networkError && transaction.orderId) {
        // Network error was set but no countdown was paused - just resume polling
        setIsCheckingPayment(true)
        setNetworkError(false)
        logActivity('Network reconnected during payment - resuming status checks', {
          orderId: transaction.orderId
        })
      }
    }
  }, [isOnline])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment && isOnline ? 5000 : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    if (!transaction.expiryTime || !transaction.serverTime || !isMounted.current) {
      return
    }

    // Don't check payment status if offline
    if (!isOnline) {
      if (isButtonClick) {
        setNetworkError(true)
        cogoToast.error(t('payment.networkError', 'Network error occurred'))
      }
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      // If payment has expired, update the UI
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          logActivity('Payment expired.', {
            orderId: transaction.orderId,
            expiryTime: transaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })
          stateUploader.uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(async () => {
          setIsCheckingPayment(false)
          cogoToast.success(t('payment.successMessage', 'Payment successful!'))
          logActivity('Payment successful.', {
            orderId: transaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'settlement'
            }
          })
          stateUploader.uploadSessionState()
          const filterSettingsForPayment = globalConfig?.pages.filter.settings
          const applyAfterCaptureForPayment = filterSettingsForPayment?.applyAfterCapture || false
          let nextPage = welcomeSettings?.startWithPayment
            ? applyAfterCaptureForPayment
              ? 'frame'
              : 'filter'
            : 'capture'
          // Check for secondary camera - only go to camera if startWithPayment is true
          // (because if startWithPayment is false, camera was already selected in Welcome)
          if (
            welcomeSettings?.startWithPayment &&
            selectedSecondaryCamera &&
            (await unifiedCameraUtils.isDeviceResponding('secondary'))
          ) {
            nextPage = 'camera'
          }
          navigate(`/${projectId}/${nextPage}`)
        })
      } else if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error(t('payment.notSuccessful', 'Payment not successful. Please try again.'))
          }
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error((e as Error).message)
          }
        })
      }
    }
  }

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenSkipPaymentGuide')
    if (!hasSeenGuide) {
      setShowSkipGuide(true)
      localStorage.setItem('hasSeenSkipPaymentGuide', 'true')

      // Auto-hide the tooltip after 10 seconds
      const timer = setTimeout(() => {
        setShowSkipGuide(false)
      }, 10000)

      return (): void => clearTimeout(timer)
    }
    return
  }, [])

  /**
   * Pick the next page after a successful customer voucher / operator PIN
   * settles the transaction. Lifted out so both paths route identically.
   * Mirrors the navigation choice in `handleCodeEntry` below.
   */
  const navigateAfterSettlement = async (): Promise<void> => {
    const customPath = welcomeSettings?.customPath
    const filterSettings = globalConfig?.pages.filter.settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    let nextPage =
      customPath === 'booth-daughter'
        ? 'capture'
        : welcomeSettings?.startWithPayment
          ? applyAfterCapture
            ? 'frame'
            : 'filter'
          : 'capture'
    if (
      welcomeSettings?.startWithPayment &&
      selectedSecondaryCamera &&
      (await unifiedCameraUtils.isDeviceResponding('secondary'))
    ) {
      nextPage = 'camera'
    }
    navigate(`/${projectId}/${nextPage}`)
  }

  /**
   * Customer voucher path on the Payment page (NOT the operator-PIN
   * bypass). Validates against `/api/promos/apply`; cash voucher settles
   * the transaction and navigates forward, discount voucher displays the
   * residual amount and asks the customer to settle offline with the
   * operator. Same contract as view/Checkout's `handlePromoEnter`.
   */
  const handleVoucherSubmit = async (code: string): Promise<void> => {
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      // `/api/promos/apply` expects the subtotal in the project's NATIVE
      // currency (voucher `value`/`minPurchase`/`maxPurchase` are stored
      // ringgit-shape for MYR projects after the Rp→RM migration). Prefer
      // `nativeAmount` over `transaction.amount` — the latter holds the
      // gateway SETTLEMENT amount (IDR for cross-border QRIS) once a
      // payment attempt has settled the session writer below. Today this
      // path is only reachable in voucher-only regions where no API call
      // has fired, but the defensive fallback chain keeps it safe if the
      // flow ever changes.
      const subtotal = Math.max(
        0,
        transaction.nativeAmount ?? transaction.amount ?? getDefaultPrice()
      )
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: trimmed,
          projectId: projectId || '',
          amount: Math.round(subtotal)
        })
      })
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      const isCash = Boolean(isValid && data.isCash)
      if (!isValid) {
        const msg =
          data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }
      if (isCash) {
        logActivity('Payment cash voucher applied', { code: trimmed, amount: subtotal })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        setSessionState({
          transaction: {
            status: 'settlement',
            currency: projectCurrency,
            voucherCode: trimmed,
            voucherId: data.promoId,
            voucherType: 'cash',
            voucherValue: data.value
          }
        })
        stateUploader.uploadSessionState()
        await navigateAfterSettlement()
        return
      }
      // Discount voucher: we can't open a gateway in voucher-only mode,
      // so surface the residual and ask the customer to settle offline
      // with the operator. Matches the reprint-modal contract.
      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      logActivity('Payment discount voucher applied (operator settlement)', {
        code: trimmed,
        discount: apiDiscount,
        finalAmount
      })
      cogoToast.info(
        t(
          'payment.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('Voucher validation error:', error)
      const msg = t(
        'checkout.promoValidationFailed',
        'Failed to validate promo code. Please try again.'
      )
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  const handleCodeEntry = async (pin: string): Promise<void> => {
    if (pin === password) {
      // Check if it's booth-daughter custom path
      const customPath = welcomeSettings?.customPath
      const filterSettings = globalConfig?.pages.filter.settings
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      let nextPage =
        customPath === 'booth-daughter'
          ? 'capture'
          : welcomeSettings?.startWithPayment
            ? applyAfterCapture
              ? 'frame'
              : 'filter'
            : 'capture'
      // Check for secondary camera - only go to camera if startWithPayment is true
      // (because if startWithPayment is false, camera was already selected in Welcome)
      if (
        welcomeSettings?.startWithPayment &&
        selectedSecondaryCamera &&
        (await unifiedCameraUtils.isDeviceResponding('secondary'))
      ) {
        nextPage = 'camera'
      }
      useSessionStateStore
        .getState()
        .logActivity('Payment bypassed with code in offline mode', { to: nextPage })
      navigate(`/${projectId}/${nextPage}`)
      setShowCodeEntry(false)
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  const handleRetry = async (): Promise<void> => {
    // Check network status first
    if (!isOnline) {
      cogoToast.error(t('payment.offline', 'No internet connection'))
      return
    }

    setIsRetrying(true)
    setNetworkError(false)

    logActivity('Payment retry initiated', {
      isOnline,
      orderId: transaction.orderId
    })

    try {
      await initiatePayment()
    } catch (error) {
      console.error('Error retrying payment:', error)
    } finally {
      setIsRetrying(false)
    }
  }

  const renderPaymentInterface = (): JSX.Element => {
    // Show network error state if offline and payment was already initiated
    if (networkError && !isOnline && transaction.orderId) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-5'}`}>
          <div className="rounded-full bg-amber-100/90 p-4">
            <FaCreditCard className={`text-amber-700 ${paymentTight ? 'h-10 w-10' : 'h-14 w-14'}`} />
          </div>
          <div className="w-full text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.offline', 'No internet connection')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.offlineMessage', 'Please check your connection and try again.')}
            </p>
          </div>
          <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
            <Button
              text={t('payment.reload', 'Reload')}
              onClick={handleRetry}
              disabled={isRetrying}
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('payment.enterCode', 'Enter code')}
              onClick={() => setShowCodeEntry(true)}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('controls.cancelSession', 'Cancel session')}
              onClick={sessionReset}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        </div>
      )
    }

    // Show offline mode UI when offline and no payment has been initiated
    if (!isOnline && !transaction.orderId) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.offlineMode', 'Offline')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.offlineInstructionShort', 'Enter your operator code to continue.')}
            </p>
          </div>
          <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
            <Button
              text={t('payment.enterCode', 'Enter code')}
              onClick={() => setShowCodeEntry(true)}
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('controls.cancelSession', 'Cancel session')}
              onClick={sessionReset}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        </div>
      )
    }

    if (isExpired) {
      const handleRestart = (): void => {
        autoResetCountdown.stop()
        sessionReset()
      }
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaUndo
              className={paymentTight ? 'h-10 w-10' : 'h-12 w-12'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="w-full text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.expired', 'Payment code expired')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.autoRestart', 'Auto-restart in')}{' '}
              <span className="font-mono tabular-nums text-carbon">{remainingTime}</span>
            </p>
          </div>
          <Button
            text={t('payment.restart', 'Restart')}
            onClick={handleRestart}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    // Voucher-only region: no QR will ever load — show the customer
    // voucher-entry CTA in the QR's slot so the page actually offers a
    // way forward. (Standard flow's redirect from Payment → Checkout was
    // dropped: the event-template flow `startWithPayment: true` goes
    // Welcome → Payment directly and has no Checkout page in its
    // pipeline, so bouncing the customer there is jarring. Putting the
    // input in-place works in both flows.)
    if (isVoucherOnlyRegion) {
      return (
        <div
          className={`flex w-full max-w-sm flex-col items-center text-center ${paymentTight ? 'gap-3' : 'gap-4'}`}
        >
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <Button
            text={t('checkout.enterVoucher', 'Enter Voucher Code')}
            onClick={() => {
              setVoucherError('')
              setShowVoucherInput(true)
            }}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    if (paymentType === 'core' && qrUrl) {
      return (
        <img
          src={qrUrl}
          alt={t('payment.qrCodeAlt', 'QR Code')}
          className="w-full h-full object-contain"
          crossOrigin="anonymous"
        />
      )
    }

    if (paymentType === 'snap' && snapToken) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.snapReady', 'Ready to pay')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.snapInstructionShort', 'Open the secure window to complete payment.')}
            </p>
          </div>
          <Button
            text={
              snapPaymentOpened
                ? t('payment.snapOpening', 'Opening…')
                : t('payment.openSnap', 'Open payment')
            }
            onClick={openSnapPayment}
            disabled={snapPaymentOpened}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    if (paymentType === 'redirect' && redirectUrl) {
      // Straight-redirect flow: the fullscreen frameless ToyyibPay popup
      // is opened automatically via the auto-open useEffect, so users
      // never need to interact with the page underneath. We only render
      // a minimal Spinner here (which the popup covers anyway). The only
      // exception is a hard failure to open the popup — in that case
      // surface the error with a retry button so the user isn't stuck
      // on a forever-spinner with no way out.
      if (billWindowError) {
        return (
          <div
            className={`flex w-full max-w-sm flex-col items-center text-center ${paymentTight ? 'gap-3' : 'gap-4'}`}
          >
            <div
              className="rounded-full p-4"
              style={{ backgroundColor: `${primaryColor}18` }}
            >
              <FaCreditCard
                className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
                style={{ color: primaryColor }}
              />
            </div>
            <p
              className={`text-red-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}
            >
              {billWindowError}
            </p>
            <Button
              text={t('payment.popupRetry', 'Retry')}
              onClick={() => openBillWindow()}
              disabled={billWindowOpen}
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        )
      }
      return <Spinner />
    }

    return <Spinner />
  }

  return (
    <BackgroundContainer
      className={`flex h-full min-h-0 items-center justify-center ${paymentNarrowLandscape ? 'overflow-hidden p-2' : 'p-4'}`}
      data-page="payment"
    >
      {showSkipGuide && (
        <div
          className={`absolute z-50 max-w-xs rounded-lg bg-gray-200 shadow-lg ${paymentTight ? 'top-8 right-4 p-3' : 'top-16 right-12 p-4'}`}
        >
          <div className="relative">
            <div className="absolute -top-6 right-4 w-4 h-4 bg-gray-200 transform rotate-45" />
            <p className="text-sm text-gray-700">
              {t(
                'payment.skipGuide',
                'Need to skip payment? Click the money icon in the top right corner and enter the PIN.'
              )}
            </p>
            <button
              onClick={(): void => setShowSkipGuide(false)}
              className="absolute -top-2 -right-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {/*
        Straight-redirect flow (currently toyyibPay): pressing
        "Proceed to Payment" on Checkout should land users directly on the
        gateway's hosted bill page. The fullscreen frameless popup is the
        only UI they should see, so we hide the entire payment panel (logo
        / amount / timer / gateway tagline). Polling, countdown and the
        navigation-on-settlement hooks still run regardless of what's
        rendered.

        We gate this on the actual returned flow rather than on the user's
        email so that if the backend ever falls back to a different payment
        type for a Malaysian user (e.g. Midtrans core/QR), they still get
        the full QR UI instead of being stuck on a spinner.

        While the create-bill request is in flight (paymentType still at
        its default 'core' and no qrUrl yet), keep the chrome hidden for
        Malaysian users so the brief loading moment matches the popup
        background, not the QRIS panel.

        The panel re-appears for recoverable states — popup-open failure
        or session expiry — so the user always has a way to retry/restart.
      */}
      {((): boolean => {
        const isToyyibpayRedirect =
          activeProvider === 'toyyibpay' && paymentType === 'redirect'
        // Initial loading window: `/api/payments` is still in flight and we
        // don't yet know which gateway will be returned. Hide the QRIS-
        // themed panel for everyone during this window so that non-QRIS
        // rails (today: ToyyibPay → redirect popup) never flash the QRIS
        // logo / countdown / empty QR box on the way to the gateway's
        // hosted page. QRIS rails just see the panel appear once we know
        // it's a QRIS rail; the QR slot is then ready almost immediately.
        //
        // Gate this only when there's actually a payment call to wait for —
        // voucher-only regions, offline mode and the network-error retry
        // panel all manage their own UI and must keep rendering inside the
        // chrome (they each have a working `paymentInitialized` flip in
        // `initiatePayment` so this never gets stuck).
        const isAwaitingPaymentInit =
          !paymentInitialized &&
          !isVoucherOnlyRegion &&
          isOnline &&
          !networkError &&
          transaction.status !== 'settlement'
        const useStraightRedirectUI =
          (isToyyibpayRedirect || isAwaitingPaymentInit) &&
          !isExpired &&
          !billWindowError
        return useStraightRedirectUI
      })() ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <GlassPanel className={`${paymentGlass} text-carbon`}>
          <div className={`flex items-center ${paymentTight ? 'gap-2' : 'gap-4'}`}>
            {/*
              QRIS logo only renders when the customer is actually going
              to use QRIS — that's Midtrans (default Dreambooth or the
              operator's custom Midtrans wallet) on the QR rail. Hidden
              for voucher-only regions (no payment happens), ToyyibPay
              (uses DuitNow QR, not QRIS), and Snap (multi-method form).
              Mid-load on a non-Midtrans rail we also hide it.
            */}
            {paymentType === 'core' &&
              activeProvider === 'midtrans' &&
              !isVoucherOnlyRegion && (
                <img
                  src={'/assets/images/qris.png'}
                  alt={t('payment.qrisLogoAlt', 'QRIS logo')}
                  className={paymentTight ? 'h-5' : 'h-8'}
                />
              )}
            <h1 className={`font-medium text-carbon ${paymentTitle}`}>{globalConfig?.title}</h1>
          </div>
          {((): React.ReactElement => {
            // Show the project-native price (e.g. RM 19.00) as the headline.
            // `transaction.amount` is the gateway SETTLEMENT amount after
            // `/api/payments` resolves — IDR ~67014 on cross-border QRIS for
            // an MYR project — and rendering that with the project's symbol
            // produces "RM 67,014", a 3500× price-anxiety bug that drives
            // customers to retry-pay repeatedly. `transaction.nativeAmount`
            // is preserved exactly for this display path; fall back to
            // `transaction.amount` for the pre-`/api/payments` window
            // (where it's still the native value from Checkout) and to
            // `getDefaultPrice()` as a last resort.
            const nativeAmount =
              transaction.nativeAmount ?? transaction.amount ?? getDefaultPrice()
            // When the gateway settles in a different currency (cross-border
            // QRIS bills IDR for a non-IDR project), show the actual charge the
            // payment API returned alongside the native price.
            const chargedInOtherCurrency =
              chargeInfo !== null && chargeInfo.currency !== projectCurrency
            return (
              <div className="flex flex-col items-center">
                <h1 className={`font-bold text-carbon ${paymentTitle}`}>
                  {formatAmount(nativeAmount, projectCurrency)}
                </h1>
                {chargedInOtherCurrency && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formatAmount(chargeInfo!.amount, chargeInfo!.currency)}
                  </p>
                )}
              </div>
            )
          })()}
          <div
            className={`relative flex items-center justify-center ${paymentType === 'redirect' ? paymentRedirectBox : paymentQrBox}`}
          >
            {renderPaymentInterface()}
          </div>
          {/*
            Hint copy + the polling timer only make sense when an actual
            payment is being awaited. In voucher-only mode nothing is
            being polled; the customer just needs to type the code, so
            we drop both lines to keep the panel focused.
          */}
          {!isVoucherOnlyRegion && (
            <>
              <p className={`mt-2 ${paymentHint}`}>
                {paymentType === 'core'
                  ? t('payment.automaticCheck', 'Payment status will be checked automatically.')
                  : paymentType === 'snap'
                    ? t('payment.snapCheck', 'Complete payment in the popup window.')
                    : t(
                        'payment.redirectCheck',
                        'Opening secure payment window. Status will update automatically.'
                      )}
              </p>
              <p className={`mt-2 ${paymentRemainingTimeClass}`}>{remainingTime}</p>
            </>
          )}
          {paymentType === 'snap' && (
            <p className="text-sm text-gray-500 text-center">
              {t('payment.snapNote', 'Using secure Midtrans payment gateway')}
            </p>
          )}
          {paymentType === 'redirect' && (
            <p className="text-sm text-gray-500 text-center">
              {t('payment.redirectNote', 'Using secure payment gateway')}{' '}
              <span className="font-medium">{activeProvider.toUpperCase()}</span>
            </p>
          )}
        </GlassPanel>
      )}

      {/*
        Operator-PIN bypass (offline-mode override). 6-digit numeric,
        matched against the operator's settings password. Reserved for
        operator-driven flow — explicitly NOT used for the customer
        voucher-only path (the OnScreenKeyboard below handles that).
      */}
      <PinModal
        isOpen={showCodeEntry}
        onClose={() => setShowCodeEntry(false)}
        onSubmit={handleCodeEntry}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t(
          'payment.codeEntry.subtitle',
          'Enter 6-digit code to bypass payment in offline mode'
        )}
      />

      {/*
        Customer voucher entry: alphanumeric code validated against
        `/api/promos/apply`. Drives the voucher-only-region flow without
        leaving the Payment page so the event-template (Welcome →
        Payment, no Checkout step) works the same as the standard flow.
      */}
      {showVoucherInput && (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t(
            'checkout.voucherCodePlaceholder',
            'Type or paste your code'
          )}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      )}
    </BackgroundContainer>
  )
}

export default PaymentPage
